import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { UnauthorizedError } from '../services/errors'
import { redirectToLogin, redirectToLogout } from '../services/portal'
import { getMe, getMySessions, type Me, type UserSessionBrief } from '../services/adminApi'
import { isLocalhost, localApiKey } from '../lib/viteEnv'

// True when running locally (vite dev OR vite build + go run) without a
// configured LOCAL_DEV_API_KEY. Forces the splash in both local flows so
// new contributors don't get an infinite redirect loop.
const DEV_KEY_MISSING = isLocalhost && !localApiKey

function LocalDevKeyMissing() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f0f0f', fontFamily: 'monospace' }}>
      <div style={{ maxWidth: 480, padding: '2rem', border: '1px solid #f59e0b', borderRadius: 8, background: '#1a1a1a', color: '#e5e5e5' }}>
        <div style={{ color: '#f59e0b', fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>⚠ Local dev: API key required</div>
        <p style={{ color: '#a3a3a3', marginBottom: '1rem', lineHeight: 1.5 }}>
          The SSO cookie is set on the staging domain and won't be sent cross-domain to localhost.
        </p>
        <p style={{ color: '#e5e5e5', marginBottom: '0.5rem' }}>Add to <code>.env</code> and restart the dev server:</p>
        <pre style={{ background: '#0f0f0f', padding: '0.75rem', borderRadius: 4, color: '#86efac', fontSize: '0.85rem', margin: 0 }}>
          LOCAL_DEV_API_KEY=aos_...
        </pre>
        <p style={{ color: '#737373', fontSize: '0.8rem', marginTop: '1rem', marginBottom: 0 }}>
          Mint a key in the <strong>customer portal</strong> (API Keys page).
          Convorcher verifies it via avatar-auth-service.
        </p>
      </div>
    </div>
  )
}

// UserContext is the auth state surface for Convorcher's admin web. It used
// to manage a localStorage-stored X-API-Key; now it consumes the
// avataros_session cookie set by portal SSO. The hook name and isAdmin
// shape are preserved so existing pages keep working with minimal churn.
//
// Flow:
//   - On mount, call GET /api/me. The backend reads the session cookie.
//   - On 401, redirect to the portal /login?return_to=<convorcher URL>.
//   - On success, expose `me` and `isAdmin` for the UI.
//   - `logout()` redirects to portal /logout?return_to=<convorcher home>.
//     Portal hits auth-service /auth/logout, clears the cookie, redirects back.

interface UserContextType {
  me: Me | null
  isLoggedIn: boolean
  isAdmin: boolean
  loading: boolean
  sessions: UserSessionBrief[]
  loginError: string
  refreshSessions: () => Promise<void>
  logout: () => void
}

const UserContext = createContext<UserContextType>(null!)

export function UserProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessions, setSessions] = useState<UserSessionBrief[]>([])
  const [loginError] = useState('')

  const isLoggedIn = me !== null
  const isAdmin = me?.is_admin ?? false

  async function refreshSessions() {
    if (!me) return
    try {
      const result = await getMySessions()
      setSessions(result)
    } catch {
      // best-effort; ignore
    }
  }

  function logout() {
    // Top-level navigation — the portal owns the cookie + session. No local
    // state to clear; this component will unmount during navigation.
    redirectToLogout()
  }

  useEffect(() => {
    if (DEV_KEY_MISSING) return
    let cancelled = false
    ;(async () => {
      try {
        const result = await getMe()
        if (cancelled) return
        setMe(result)
        try {
          const sessionList = await getMySessions()
          if (!cancelled) setSessions(sessionList)
        } catch {
          // sessions are non-critical; ignore
        }
      } catch (err) {
        if (cancelled) return
        if (err instanceof UnauthorizedError) {
          redirectToLogin()
          return
        }
        // Network error etc. — surface as logged-out for now; the UI will
        // render the splash and the user can retry.
        setMe(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (DEV_KEY_MISSING) return <LocalDevKeyMissing />

  return (
    <UserContext.Provider value={{
      me,
      isLoggedIn,
      isAdmin,
      loading,
      sessions,
      loginError,
      refreshSessions,
      logout,
    }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => useContext(UserContext)
